/**
 * Brand marks from the official Caffeina brand kit.
 *
 * Inlined rather than loaded as files so they cost no extra request and can
 * follow the surrounding CSS `color` — the kit ships one theme per background
 * (dark mark on light, ivory on dark), and `currentColor` lets a single copy
 * serve both. The full-bleed background rect from the kit is deliberately
 * dropped: these sit on the site's own cream.
 *
 * The molecular linework is reproduced exactly as supplied — the kit forbids
 * stretching, rotating or altering it, so the viewBox only crops whitespace.
 */

interface Props {
  /** Accessible name; omit for decorative use alongside visible text. */
  title?: string;
  className?: string;
}

/**
 * The signature lockup: molecular isotype to the left of the wordmark, the
 * pairing the kit shows. Both marks scale from the container's font-size, so
 * one number resizes the whole thing and their relative proportions can never
 * drift apart.
 *
 * Only the lockup carries the accessible name — the two marks inside are one
 * logo, and announcing each separately would read as two images.
 */
export function Logo({ title, className }: Props) {
  return (
    <span
      className={className ? `logo ${className}` : 'logo'}
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
    >
      <Isotype className="logo-iso" />
      <Wordmark className="logo-word" />
    </span>
  );
}

export function Wordmark({ title, className }: Props) {
  return (
    <svg
      className={className}
      viewBox="238.7 324.0 1159.3 229.4"
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
    >
      <g fill="currentColor">
          <g transform="translate(235 545) scale(0.09485952 -0.09485952)">
            <path transform="translate(0 0)" d="M1354 1341V1507H1546V1024H1350Q1276 1169 1147.0 1250.0Q1018 1331 864 1331Q715 1331 598.0 1252.5Q481 1174 415.5 1042.5Q350 911 350 760Q350 604 418.5 472.0Q487 340 607.0 261.0Q727 182 874 182Q1055 182 1194.0 292.5Q1333 403 1397 606L1589 504Q1511 252 1313.5 113.5Q1116 -25 850 -25Q631 -25 461.0 79.5Q291 184 196.5 360.5Q102 537 102 748Q102 977 198.5 1156.0Q295 1335 465.0 1433.5Q635 1532 854 1532Q1001 1532 1125.0 1483.0Q1249 1434 1354 1341Z"/>
            <path transform="translate(1668 0)" d="M1042 1325 1450 188H1618V0H1024V188H1221L1104 530H557L440 188H631V0H33V188H215L621 1325H440V1507H1223V1325ZM836 1325H829L616 707H1044Z"/>
            <path transform="translate(3299 0)" d="M1020 1067V1319H455V872H942V684H455V188H647V0H37V188H227V1319H37V1507H1206V1067Z"/>
            <path transform="translate(4530 0)" d="M1020 1067V1319H455V872H942V684H455V188H647V0H37V188H227V1319H37V1507H1206V1067Z"/>
            <path transform="translate(5761 0)" d="M1100 1067V1319H455V870H1016V682H455V188H1100V463H1286V0H33V188H227V1319H33V1507H1286V1067Z"/>
            <path transform="translate(7101 0)" d="M457 1319V188H621V0H66V188H229V1319H66V1507H621V1319Z"/>
            <path transform="translate(7769 0)" d="M1221 1319H1006V1507H1624V1319H1403V-16H1169L446 1272V188H662V0H45V188H260V1319H45V1507H559L1221 336Z"/>
            <path transform="translate(9418 0)" d="M1042 1325 1450 188H1618V0H1024V188H1221L1104 530H557L440 188H631V0H33V188H215L621 1325H440V1507H1223V1325ZM836 1325H829L616 707H1044Z"/>
          </g>
          <path fill-rule="evenodd" d="M1348 330A44 44 0 1 1 1348 418A44 44 0 1 1 1348 330ZM1348 333A41 41 0 1 0 1348 415A41 41 0 1 0 1348 333Z"/>
          <path transform="translate(1324.23529 398.00000) scale(0.06722689 -0.06722689)" d="M140 375V656H394Q472 656 510.0 618.0Q548 580 548 514Q548 481 535.5 455.5Q523 430 502.0 412.0Q481 394 453.0 384.5Q425 375 394 375ZM72 714V0H140V317H398Q446 319 473.5 301.0Q501 283 515.5 253.5Q530 224 534.5 186.5Q539 149 541 111Q542 100 543.0 85.0Q544 70 545.5 54.5Q547 39 550.5 24.5Q554 10 560 0H635Q624 13 617.5 36.5Q611 60 608.0 86.5Q605 113 604.0 139.0Q603 165 602 183Q600 215 593.5 242.5Q587 270 572.5 291.0Q558 312 535.0 325.5Q512 339 477 344V346Q546 360 581.0 410.0Q616 460 616 528Q616 576 598.5 611.5Q581 647 551.5 669.5Q522 692 482.5 703.0Q443 714 399 714Z"/>
        </g>
    </svg>
  );
}

export function Isotype({ title, className }: Props) {
  return (
    <svg
      className={className}
      viewBox="241.4 279.9 717.4 642.2"
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
    >
      <g transform="translate(-24 -170) scale(1.2)" fill="currentColor">
          <path d="M444.900,379.900 L449.100,379.900 L449.100,498.100 L444.900,498.100 Z M462.900,379.900 L467.100,379.900 L467.100,499.100 L462.900,499.100 Z M456.785,494.136 L458.864,497.785 L362.215,552.864 L360.136,549.215 Z M453.138,497.793 L455.207,494.138 L587.862,569.207 L585.793,572.862 Z M254.137,529.790 L256.210,526.137 L296.863,549.210 L294.790,552.863 Z M325.900,600.900 L330.100,600.900 L330.100,712.100 L325.900,712.100 Z M325.130,718.765 L327.235,715.130 L425.870,772.235 L423.765,775.870 Z M323.796,715.139 L325.861,718.796 L237.204,768.861 L235.139,765.204 Z M319.744,726.125 L321.875,729.744 L228.256,784.875 L226.125,781.256 Z M453.900,823.900 L458.100,823.900 L458.100,905.100 L453.900,905.100 Z M583.845,572.736 L582.264,568.845 L682.155,528.264 L683.736,532.155 Z M582.900,567.900 L587.100,567.900 L587.100,721.100 L582.900,721.100 Z M564.900,579.900 L569.100,579.900 L569.100,710.100 L564.900,710.100 Z M585.788,716.137 L587.863,719.788 L489.212,775.863 L487.137,772.212 Z M582.411,720.455 L583.545,716.411 L694.589,747.545 L693.455,751.589 Z M739.787,479.864 L736.136,477.787 L771.213,416.136 L774.864,418.213 Z M738.039,549.766 L741.234,547.039 L813.961,632.234 L810.766,634.961 Z M810.285,629.118 L813.882,631.285 L758.715,722.882 L755.118,720.715 Z M789.454,634.081 L792.919,636.454 L740.546,712.919 L737.081,710.546 Z"/>
        </g>
    </svg>
  );
}
